import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'pt' | 'en';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  pt: {
    // Navigation & General
    title: 'Diagnóstico do Module Federation Remoto',
    runDiagnostic: 'Executar Diagnóstico',
    runningDiagnostic: 'Executando Diagnóstico...',
    testCompleted: 'Teste concluído!',
    diagnosticFailed: 'Diagnóstico falhou:',

    // Debug Info
    remoteDebugInfo: 'Informações de Debug do Remoto',
    url: 'URL',
    accessible: 'Acessível',
    cors: 'CORS',
    contentType: 'Tipo de Conteúdo',
    hasInit: 'Tem Init',
    hasGet: 'Tem Get',
    size: 'Tamanho',
    error: 'Erro',
    yes: 'Sim',
    no: 'Não',
    ok: 'OK',
    check: 'Verificar',
    na: 'N/A',
    bytes: 'bytes',

    // Console Logs
    logs: 'Logs do Console',
    noLogs: 'Nenhum log capturado',

    // Tips
    tips: 'Dicas',
    tip1: 'Verifique se o remoto está acessível e retorna conteúdo JavaScript',
    tip2: 'Verifique se os cabeçalhos CORS permitem requisições cross-origin',
    tip3: 'Certifique-se de que o remoto exporta as funções init e get',
    tip4: 'Verifique o console do navegador para mensagens de erro detalhadas',
    tip5: 'Verifique se as versões das dependências compartilhadas são compatíveis',

    // Test Messages
    startingTest: '��� Iniciando teste do Module Federation...',
    testingRemoteEntry: '1. Testando carregamento do remote entry...',
    remoteEntryLoaded: '✅ Remote entry carregado com sucesso',
    initFunctionFound: '2. Função init encontrada, chamando...',
    remoteInitialized: '✅ Remoto inicializado com sucesso',
    noInitFunction: '❌ Função init não encontrada',
    getFunctionFound: '3. Função get encontrada, testando carregamento de módulo...',
    moduleFactoryObtained: '✅ Factory do módulo AuthContext obtida',
    moduleLoaded: '✅ Módulo AuthContext carregado:',
    failedToLoadModule: '❌ Falha ao carregar AuthContext:',
    noGetFunction: '❌ Função get não encontrada',
    testFailed: '❌ Teste falhou:',

    // Controls
    darkMode: 'Modo Escuro',
    lightMode: 'Modo Claro',
    portuguese: 'Português',
    english: 'English',

    // MIME Type Diagnostics
    networkDiagnostic: 'Diagnóstico de Rede',
    status: 'Status',
    mimeType: 'Tipo MIME',
    redirected: 'Redirecionado',
    finalUrl: 'URL Final',
    mimeTypeErrors: 'Erros de Tipo MIME',
    mimeIssueDetected: 'Problema de Tipo MIME Detectado',
    mimeIssueDescription: 'O servidor está retornando conteúdo HTML em vez de JavaScript. Isso geralmente indica:',
    mimeIssueCause1: 'Remote pode não estar acessível',
    mimeIssueCause2: 'O build remoto não foi implantado corretamente',
    mimeIssueCause3: 'Os caminhos dos assets estão incorretos',
    mimeIssueCause4: 'Configuração de tipo MIME do servidor está errada',
    mimeIssueCause5: 'Roteamento do servidor está redirecionando requisições de assets para index.html',
    quickFixSolutions: 'Soluções Rápidas',
    solution1:
      'Verificar configuração do servidor: Certificar que assets estáticos são servidos com tipos MIME corretos',
    solution2: 'Verificar saída do build: Checar se os arquivos existem nos locais esperados',
    solution3: 'Revisar roteamento: Certificar que rotas de assets não fazem fallback para index.html',
    solution4: 'Testar localmente: Tente executar a aplicação remota localmente para isolar o problema',
    solution5:
      'Cabeçalhos CORS: Verificar se o compartilhamento de recursos cross-origin está configurado corretamente',
    expected: 'Esperado',
    received: 'Recebido',

    // Quick Test & Advanced Options
    quickHealthCheck: 'Verificação Rápida de Saúde',
    retest: 'Testar Novamente',
    testing: 'Testando...',
    remoteEntry: 'Entrada Remota',
    assets: 'Assets',
    advancedOptions: 'Opções Avançadas',
    remoteUrl: 'URL Remota',
    quickSelect: 'Seleção Rápida',
    checkBrowserCompatibility: 'Verificar Compatibilidade do Navegador',
    resultsWillBeLogged: 'Resultados serão logados no console',
    origin: 'Origem',
    protocol: 'Protocolo',
    userAgent: 'User Agent',
    cookies: 'Cookies',
    enabled: 'Habilitado',
    disabled: 'Desabilitado',

    // Real-time Monitor
    realTimeMonitor: 'Monitor em Tempo Real',
    stop: 'Parar',
    monitor: 'Monitorar',
    clear: 'Limpar',
    monitoringActive: 'Monitoramento ativo',
    monitoringInactive: 'Monitoramento inativo',
    entries: 'entradas',
    all: 'Todos',
    noLogsYet: 'Nenhum log ainda...',
    startMonitoringToSee: 'Inicie o monitoramento para ver logs em tempo real',

    // Intelligent Analysis
    intelligentAnalysis: 'Análise Inteligente',
    patternDetected: 'Padrão Detectado',
    fallbackRouting: 'Roteamento de Fallback para SPA',
    fallbackRoutingDescription:
      'O servidor está redirecionando todas as requisições de assets para o index.html, típico de SPAs mal configuradas.',
    fallbackSolution1: 'Configure exceções no servidor para servir assets estáticos diretamente',
    fallbackSolution2: 'Ajuste as regras de rewrite/redirect para excluir arquivos .js e .css',
    fallbackSolution3: 'Verifique se a pasta de build/dist está sendo servida corretamente',
    moduleScriptIssue: 'Problema com Module Scripts ES6',
    moduleScriptDescription: 'Scripts ES6 modules requerem tipo MIME application/javascript exato para funcionar.',
    moduleScriptSolution1: 'Configure o servidor para servir .js com Content-Type: application/javascript',
    moduleScriptSolution2: 'Verifique se o servidor não está forçando text/html para todos os recursos',
    moduleScriptSolution3: 'Considere usar um servidor de desenvolvimento como serve ou http-server',
    cssLoadingIssue: 'Problema no Carregamento de CSS',
    cssLoadingDescription: 'Arquivos CSS não estão sendo servidos com o tipo MIME correto.',
    cssLoadingSolution1: 'Configure o servidor para servir .css com Content-Type: text/css',
    cssLoadingSolution2: 'Verifique se existe um arquivo .htaccess interferindo',
    cssLoadingSolution3: 'Teste com um servidor local para isolar o problema',
    generalMimeAnalysis: 'Analisando padrões nos erros de MIME type para fornecer sugestões específicas...',
  },
  en: {
    // Navigation & General
    title: 'Remote Module Federation Diagnostic',
    runDiagnostic: 'Run Diagnostic',
    runningDiagnostic: 'Running Diagnostic...',
    testCompleted: 'Test completed!',
    diagnosticFailed: 'Diagnostic failed:',

    // Debug Info
    remoteDebugInfo: 'Remote Debug Info',
    url: 'URL',
    accessible: 'Accessible',
    cors: 'CORS',
    contentType: 'Content Type',
    hasInit: 'Has Init',
    hasGet: 'Has Get',
    size: 'Size',
    error: 'Error',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    check: 'Check',
    na: 'N/A',
    bytes: 'bytes',

    // Console Logs
    logs: 'Console Logs',
    noLogs: 'No logs captured',

    // Tips
    tips: 'Tips',
    tip1: 'Check that the remote is accessible and returns JavaScript content',
    tip2: 'Verify CORS headers allow cross-origin requests',
    tip3: 'Ensure the remote exports both init and get functions',
    tip4: 'Check browser console for detailed error messages',
    tip5: 'Verify that shared dependencies versions are compatible',

    // Test Messages
    startingTest: '��� Starting Module Federation test...',
    testingRemoteEntry: '1. Testing remote entry loading...',
    remoteEntryLoaded: '✅ Remote entry loaded successfully',
    initFunctionFound: '2. Init function found, calling...',
    remoteInitialized: '✅ Remote initialized successfully',
    noInitFunction: '❌ No init function found',
    getFunctionFound: '3. Get function found, testing module loading...',
    moduleFactoryObtained: '✅ AuthContext module factory obtained',
    moduleLoaded: '✅ AuthContext module loaded:',
    failedToLoadModule: '❌ Failed to load AuthContext:',
    noGetFunction: '❌ No get function found',
    testFailed: '❌ Test failed:',

    // Controls
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    portuguese: 'Português',
    english: 'English',

    // MIME Type Diagnostics
    networkDiagnostic: 'Network Diagnostic',
    status: 'Status',
    mimeType: 'MIME Type',
    redirected: 'Redirected',
    finalUrl: 'Final URL',
    mimeTypeErrors: 'MIME Type Errors',
    mimeIssueDetected: 'MIME Type Issue Detected',
    mimeIssueDescription: 'The server is returning HTML content instead of JavaScript. This usually indicates:',
    mimeIssueCause1: 'Remote may not be accessible',
    mimeIssueCause2: 'The remote build is not deployed correctly',
    mimeIssueCause3: 'Asset paths are incorrect',
    mimeIssueCause4: 'Server MIME type configuration is wrong',
    mimeIssueCause5: 'Server routing is redirecting asset requests to index.html',
    quickFixSolutions: 'Quick Fix Solutions',
    solution1: 'Check server configuration: Ensure static assets are served with correct MIME types',
    solution2: 'Verify build output: Check if files exist in the expected locations',
    solution3: "Review routing: Make sure asset routes don't fallback to index.html",
    solution4: 'Test locally: Try running the remote app locally to isolate the issue',
    solution5: 'CORS headers: Verify cross-origin resource sharing is properly configured',
    expected: 'Expected',
    received: 'Received',

    // Quick Test & Advanced Options
    quickHealthCheck: 'Quick Health Check',
    retest: 'Retest',
    testing: 'Testing...',
    remoteEntry: 'Remote Entry',
    assets: 'Assets',
    advancedOptions: 'Advanced Options',
    remoteUrl: 'Remote URL',
    quickSelect: 'Quick Select',
    checkBrowserCompatibility: 'Check Browser Compatibility',
    resultsWillBeLogged: 'Results will be logged to console',
    origin: 'Origin',
    protocol: 'Protocol',
    userAgent: 'User Agent',
    cookies: 'Cookies',
    enabled: 'Enabled',
    disabled: 'Disabled',

    // Real-time Monitor
    realTimeMonitor: 'Real-time Monitor',
    stop: 'Stop',
    monitor: 'Monitor',
    clear: 'Clear',
    monitoringActive: 'Monitoring active',
    monitoringInactive: 'Monitoring inactive',
    entries: 'entries',
    all: 'All',
    noLogsYet: 'No logs yet...',
    startMonitoringToSee: 'Start monitoring to see real-time logs',

    // Intelligent Analysis
    intelligentAnalysis: 'Intelligent Analysis',
    patternDetected: 'Pattern Detected',
    fallbackRouting: 'SPA Fallback Routing',
    fallbackRoutingDescription:
      'Server is redirecting all asset requests to index.html, typical of misconfigured SPAs.',
    fallbackSolution1: 'Configure server exceptions to serve static assets directly',
    fallbackSolution2: 'Adjust rewrite/redirect rules to exclude .js and .css files',
    fallbackSolution3: 'Verify that build/dist folder is being served correctly',
    moduleScriptIssue: 'ES6 Module Script Issue',
    moduleScriptDescription: 'ES6 module scripts require exact application/javascript MIME type to work.',
    moduleScriptSolution1: 'Configure server to serve .js with Content-Type: application/javascript',
    moduleScriptSolution2: 'Verify server is not forcing text/html for all resources',
    moduleScriptSolution3: 'Consider using a development server like serve or http-server',
    cssLoadingIssue: 'CSS Loading Issue',
    cssLoadingDescription: 'CSS files are not being served with correct MIME type.',
    cssLoadingSolution1: 'Configure server to serve .css with Content-Type: text/css',
    cssLoadingSolution2: 'Check if .htaccess file is interfering',
    cssLoadingSolution3: 'Test with local server to isolate the issue',
    generalMimeAnalysis: 'Analyzing MIME type error patterns to provide specific suggestions...',
  },
};

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('clarke-language');
    return (saved as Language) || 'pt';
  });

  useEffect(() => {
    localStorage.setItem('clarke-language', language);
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev: 'pt' | 'en') => (prev === 'pt' ? 'en' : 'pt'));
  };

  const t = (key: string): string => {
    const translation = translations[language as keyof typeof translations] as Record<string, string>;
    return translation[key] || key;
  };

  return <LanguageContext.Provider value={{ language, toggleLanguage, t }}>{children}</LanguageContext.Provider>;
}
